/**
 * Layout Style Presets
 * Each preset defines a complete visual style for Sidebar, Navbar, and overall layout.
 */

export const LAYOUT_STYLES = {
  classic: {
    id: 'classic',
    name: 'Classic',
    description: 'Clean white sidebar with a professional header',
    preview: {
      sidebar: '#FFFFFF',
      sidebarText: '#374151',
      header: '#FFFFFF',
      headerText: '#111827',
      accent: 'primary', // uses primaryColor
    },
    sidebar: {
      bg: '#FFFFFF',
      bgDark: '#1E293B',
      text: '#64748B',
      textHover: '#0F172A',
      textDark: '#CBD5E1',
      textHoverDark: '#F8FAFC',
      hoverBg: '#F1F5F9',
      hoverBgDark: '#334155',
      border: '#E2E8F0',
      borderDark: '#334155',
      logoStyle: 'gradient', // gradient | solid | outlined
      navItemRadius: '0.5rem',
      activeStyle: 'gradient', // gradient | solid | left-border | pill
    },
    navbar: {
      bg: '#FFFFFF',
      bgDark: '#1E293B',
      text: '#374151',
      textDark: '#E5E7EB',
      border: '#E5E7EB',
      borderDark: '#374151',
      shadow: '0 1px 3px rgba(0,0,0,0.08)',
      shadowDark: '0 1px 3px rgba(0,0,0,0.3)',
      height: '72px',
      style: 'bordered', // bordered | shadow | transparent | glass
    },
    layout: {
      contentBg: '#F8FAFC',
      contentBgDark: '#0B1120',
      sidebarWidth: '240px',
      borderRadius: '0.5rem',
    },
  },

  modern: {
    id: 'modern',
    name: 'Modern',
    description: 'Sleek design with soft shadows and rounded elements',
    preview: {
      sidebar: '#F8FAFC',
      sidebarText: '#475569',
      header: '#FFFFFF',
      headerText: '#1E293B',
      accent: 'primary',
    },
    sidebar: {
      bg: '#F8FAFC',
      bgDark: '#0F172A',
      text: '#64748B',
      textHover: '#1E293B',
      textDark: '#94A3B8',
      textHoverDark: '#F1F5F9',
      hoverBg: '#E2E8F0',
      hoverBgDark: '#1E293B',
      border: '#E2E8F0',
      borderDark: '#1E293B',
      logoStyle: 'gradient',
      navItemRadius: '1rem',
      activeStyle: 'pill',
    },
    navbar: {
      bg: '#FFFFFF',
      bgDark: '#0F172A',
      text: '#1E293B',
      textDark: '#F1F5F9',
      border: 'transparent',
      borderDark: 'transparent',
      shadow: '0 4px 20px rgba(0,0,0,0.06)',
      shadowDark: '0 4px 20px rgba(0,0,0,0.4)',
      height: '68px',
      style: 'shadow',
    },
    layout: {
      contentBg: '#F1F5F9',
      contentBgDark: '#0B1120',
      sidebarWidth: '260px',
      borderRadius: '1rem',
    },
  },

  corporate: {
    id: 'corporate',
    name: 'Corporate',
    description: 'Dark branded sidebar with light content area — enterprise feel',
    preview: {
      sidebar: '#26256b',
      sidebarText: '#c4c3e8',
      header: '#FFFFFF',
      headerText: '#111827',
      accent: 'primary',
    },
    sidebar: {
      bg: '#26256b',
      bgDark: '#1d1b56',
      text: '#c4c3e8',
      textHover: '#FFFFFF',
      textDark: '#a5a4d4',
      textHoverDark: '#FFFFFF',
      hoverBg: 'rgba(255,255,255,0.10)',
      hoverBgDark: 'rgba(255,255,255,0.08)',
      border: 'rgba(255,255,255,0.13)',
      borderDark: 'rgba(255,255,255,0.09)',
      logoStyle: 'gradient',
      navItemRadius: '0.375rem',
      activeStyle: 'left-border',
      forceDark: true,
    },
    navbar: {
      bg: '#FFFFFF',
      bgDark: '#1d1b56',
      text: '#111827',
      textDark: '#F1F5F9',
      border: '#E5E7EB',
      borderDark: 'rgba(255,255,255,0.08)',
      shadow: '0 1px 3px rgba(0,0,0,0.07), 0 4px 8px -2px rgba(0,0,0,0.04)',
      shadowDark: '0 1px 3px rgba(0,0,0,0.3)',
      height: '66px',
      style: 'bordered',
    },
    layout: {
      contentBg: '#F4F5FA',
      contentBgDark: '#13124a',
      sidebarWidth: '260px',
      borderRadius: '0.5rem',
    },
  },

  minimal: {
    id: 'minimal',
    name: 'Minimal',
    description: 'Ultra-clean with thin borders and subtle accents',
    preview: {
      sidebar: '#FFFFFF',
      sidebarText: '#6B7280',
      header: '#FFFFFF',
      headerText: '#374151',
      accent: 'primary',
    },
    sidebar: {
      bg: '#FFFFFF',
      bgDark: '#111827',
      text: '#6B7280',
      textHover: '#374151',
      textDark: '#9CA3AF',
      textHoverDark: '#F3F4F6',
      hoverBg: '#F9FAFB',
      hoverBgDark: '#1F2937',
      border: '#F3F4F6',
      borderDark: '#1F2937',
      logoStyle: 'outlined',
      navItemRadius: '0.375rem',
      activeStyle: 'solid',
    },
    navbar: {
      bg: '#FFFFFF',
      bgDark: '#111827',
      text: '#374151',
      textDark: '#E5E7EB',
      border: '#F3F4F6',
      borderDark: '#1F2937',
      shadow: 'none',
      shadowDark: 'none',
      height: '60px',
      style: 'transparent',
    },
    layout: {
      contentBg: '#FFFFFF',
      contentBgDark: '#0B1120',
      sidebarWidth: '240px',
      borderRadius: '0.375rem',
    },
  },

  executive: {
    id: 'executive',
    name: 'Executive',
    description: 'Premium dark sidebar with glass-effect header',
    preview: {
      sidebar: '#111827',
      sidebarText: '#9CA3AF',
      header: 'rgba(255,255,255,0.85)',
      headerText: '#1F2937',
      accent: 'primary',
    },
    sidebar: {
      bg: '#111827',
      bgDark: '#030712',
      text: '#9CA3AF',
      textHover: '#FFFFFF',
      textDark: '#6B7280',
      textHoverDark: '#F9FAFB',
      hoverBg: '#1F2937',
      hoverBgDark: '#111827',
      border: '#1F2937',
      borderDark: '#111827',
      logoStyle: 'gradient',
      navItemRadius: '0.625rem',
      activeStyle: 'gradient',
      forceDark: true,
    },
    navbar: {
      bg: 'rgba(255,255,255,0.85)',
      bgDark: 'rgba(15,23,42,0.85)',
      text: '#1F2937',
      textDark: '#E5E7EB',
      border: 'rgba(229,231,235,0.5)',
      borderDark: 'rgba(55,65,81,0.5)',
      shadow: '0 4px 30px rgba(0,0,0,0.05)',
      shadowDark: '0 4px 30px rgba(0,0,0,0.3)',
      height: '68px',
      style: 'glass',
      backdrop: 'blur(12px)',
    },
    layout: {
      contentBg: '#F3F4F6',
      contentBgDark: '#0B1120',
      sidebarWidth: '260px',
      borderRadius: '0.75rem',
    },
  },

  horizon: {
    id: 'horizon',
    name: 'Horizon',
    description: 'Horizontal top navigation — no sidebar, wide content',
    preview: {
      sidebar: 'transparent',
      sidebarText: 'transparent',
      header: '#FFFFFF',
      headerText: '#1E293B',
      accent: 'primary',
    },
    sidebar: {
      bg: '#FFFFFF',
      bgDark: '#0F172A',
      text: '#64748B',
      textHover: '#0F172A',
      textDark: '#94A3B8',
      textHoverDark: '#F1F5F9',
      hoverBg: '#F1F5F9',
      hoverBgDark: '#1E293B',
      border: '#E2E8F0',
      borderDark: '#334155',
      logoStyle: 'gradient',
      navItemRadius: '0.5rem',
      activeStyle: 'solid',
      sidebarMode: 'horizontal',
    },
    navbar: {
      bg: '#FFFFFF',
      bgDark: '#0F172A',
      text: '#1E293B',
      textDark: '#F1F5F9',
      border: '#E2E8F0',
      borderDark: '#334155',
      shadow: '0 1px 3px rgba(0,0,0,0.06)',
      shadowDark: '0 1px 3px rgba(0,0,0,0.3)',
      height: '64px',
      style: 'bordered',
    },
    layout: {
      contentBg: '#F8FAFC',
      contentBgDark: '#0B1120',
      sidebarWidth: '0px',
      borderRadius: '0.75rem',
    },
  },

  compact: {
    id: 'compact',
    name: 'Compact',
    description: 'Slim icon sidebar — more screen space for content',
    preview: {
      sidebar: '#1E293B',
      sidebarText: '#94A3B8',
      header: '#FFFFFF',
      headerText: '#111827',
      accent: 'primary',
    },
    sidebar: {
      bg: '#1E293B',
      bgDark: '#0F172A',
      text: '#94A3B8',
      textHover: '#FFFFFF',
      textDark: '#64748B',
      textHoverDark: '#F8FAFC',
      hoverBg: '#334155',
      hoverBgDark: '#1E293B',
      border: '#334155',
      borderDark: '#1E293B',
      logoStyle: 'gradient',
      navItemRadius: '0.625rem',
      activeStyle: 'solid',
      forceDark: true,
      sidebarMode: 'compact',
    },
    navbar: {
      bg: '#FFFFFF',
      bgDark: '#1E293B',
      text: '#111827',
      textDark: '#F1F5F9',
      border: '#E5E7EB',
      borderDark: '#334155',
      shadow: 'none',
      shadowDark: 'none',
      height: '64px',
      style: 'bordered',
    },
    layout: {
      contentBg: '#F8FAFC',
      contentBgDark: '#0B1120',
      sidebarWidth: '72px',
      borderRadius: '0.625rem',
    },
  },

  dashboard: {
    id: 'dashboard',
    name: 'Dashboard',
    description: 'Floating sidebar with inset design — data-focused layout',
    preview: {
      sidebar: '#0F172A',
      sidebarText: '#94A3B8',
      header: '#FFFFFF',
      headerText: '#0F172A',
      accent: 'primary',
    },
    sidebar: {
      bg: '#0F172A',
      bgDark: '#020617',
      text: '#64748B',
      textHover: '#F1F5F9',
      textDark: '#475569',
      textHoverDark: '#F8FAFC',
      hoverBg: '#1E293B',
      hoverBgDark: '#0F172A',
      border: '#1E293B',
      borderDark: '#0F172A',
      logoStyle: 'gradient',
      navItemRadius: '0.75rem',
      activeStyle: 'gradient',
      forceDark: true,
      sidebarMode: 'floating',
    },
    navbar: {
      bg: '#FFFFFF',
      bgDark: 'rgba(15,23,42,0.9)',
      text: '#0F172A',
      textDark: '#E2E8F0',
      border: '#E2E8F0',
      borderDark: '#334155',
      shadow: '0 1px 2px rgba(0,0,0,0.04)',
      shadowDark: '0 1px 2px rgba(0,0,0,0.2)',
      height: '64px',
      style: 'bordered',
    },
    layout: {
      contentBg: '#F1F5F9',
      contentBgDark: '#111827',
      sidebarWidth: '260px',
      borderRadius: '0.75rem',
    },
  },
};

/**
 * Get a layout style by ID, falling back to 'classic'
 */
export const getLayoutStyle = (styleId) => {
  return LAYOUT_STYLES[styleId] || LAYOUT_STYLES.classic;
};

/**
 * Get all layout style entries as an array for rendering pickers
 */
export const getAllLayoutStyles = () => {
  return Object.values(LAYOUT_STYLES);
};

export default LAYOUT_STYLES;
